import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled, { css, keyframes } from 'styled-components'
import { CircularProgress } from '@mui/material'
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined'
import { toast } from 'react-toastify'
import BpLogo from 'assets/images/bp-logo-orange.png'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { getBuildingQrLandingInfo } from 'services/qrLandingService'
import { requestAccessToBp } from 'services/BpService'
import { PATHS, PRIVATE_PATHS } from 'utils/paths'
import LocalStorageService from 'services/LocalStorageService'
import { KEYS } from 'utils/localStorage'
import SearchableSelect from 'components/SearchableSelect'
import { ROLE_LABELS } from 'utils/roles'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  background: ${({ theme }) => theme.colors.lighterGrey};

  @media (min-width: 600px) {
    align-items: center;
    padding: 40px 24px;
  }
`

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  box-shadow:
    0 10px 40px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(0, 0, 0, 0.04);
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  animation: ${fadeIn} 0.25s ease;

  @media (max-width: 576px) {
    padding: 24px 20px;
    border-radius: 14px;
  }
`

const Logo = styled.img`
  height: 34px;
  width: auto;
  align-self: center;
  margin-bottom: 4px;
`

const HeroIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.linen};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 4px;
`

const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  text-align: center;
  line-height: 1.3;
`

const Subtitle = styled.p`
  margin: 0;
  text-align: center;
  font-size: 14px;
  color: #475569;
  line-height: 1.55;
`

const InfoBlock = styled.div`
  background: #f8fafc;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid #eef2f7;
`

const InfoLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const InfoValue = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 600;
  line-height: 1.4;
`

const InfoSub = styled.div`
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const SharedButtonStyle = css`
  width: 100%;
  height: 52px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const PrimaryButton = styled.button`
  ${SharedButtonStyle}
  background: #110c2f;
  color: ${({ theme }) => theme.colors.white};

  &:hover:not(:disabled) {
    background: #36becd;
    transform: translateY(-1px);
  }
`

const SecondaryButton = styled.button`
  ${SharedButtonStyle}
  background: transparent;
  color: #110c2f;
  border: 2px solid #110c2f;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
`

const ErrorState = styled.div`
  background: ${({ theme }) => theme.colors.linen};
  color: ${({ theme }) => theme.colors.error};
  border-radius: 10px;
  padding: 14px 16px;
  font-size: 14px;
  text-align: center;
  line-height: 1.5;
`

const SuccessState = styled.div`
  background: #ecfdf5;
  color: #065f46;
  border-radius: 10px;
  padding: 16px;
  font-size: 14px;
  text-align: center;
  line-height: 1.55;
  border: 1px solid #d1fae5;
`

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`

const RELATION_OPTIONS = [
  { value: 'owner', label: 'I own a flat in this building' },
  { value: 'agent', label: 'I am a managing agent' },
  { value: 'responsible_person', label: "I'm the responsible person" },
]

const BuildingQrLanding = () => {
  const { bpId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const currentUser = useSelector(getCurrentUser)
  const [info, setInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [relationType, setRelationType] = useState('owner')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getBuildingQrLandingInfo(bpId)
      .then(res => {
        if (cancelled) return
        setInfo(res?.data?.data || null)
      })
      .catch(err => {
        if (cancelled) return
        const status = err?.response?.status
        const body = err?.response?.data?.data
        if (status === 404) {
          if (body?.unknown) {
            setError(`We couldn't find this building. The QR sticker may be out of date, or the link may be wrong. (id: ${bpId})`)
          } else {
            setError(`We couldn't find this building (id: ${bpId}). The QR sticker may be out of date.`)
          }
        } else if (status === 429) {
          setError('Too many attempts. Please wait a moment and try again.')
        } else if (!status) {
          setError("We couldn't reach the server. Check your internet connection and try again.")
        } else {
          setError(`Something went wrong loading this building (error ${status}). Please try again shortly.`)
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [bpId, loadAttempt])

  const stashRedirectAndGo = (target) => {
    LocalStorageService.set(KEYS.redirectPath, location.pathname)
    navigate(target)
  }

  const handleSubmitAccess = async () => {
    setSubmitting(true)
    try {
      await requestAccessToBp({ bpId, relationType })
      setSubmitted(true)
    } catch (err) {
      if (err?.response?.status === 422) {
        setSubmitted(true)
        toast.info("You've already requested access to this building.")
      } else {
        toast.error('Could not send your access request. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const isAuthed = !!currentUser?.id

  return (
    <Page>
      <Card>
        <Logo src={BpLogo} alt="Building Passport" />

        {loading && (
          <Centered><CircularProgress size={26} /></Centered>
        )}

        {!loading && error && (
          <>
            <ErrorState>{error}</ErrorState>
            <SecondaryButton onClick={() => setLoadAttempt(n => n + 1)}>
              Try again
            </SecondaryButton>
          </>
        )}

        {!loading && !error && info?.removed && (
          <>
            <HeroIcon><DomainOutlinedIcon style={{ fontSize: 32 }} /></HeroIcon>
            <Title>This building is no longer active</Title>
            <Subtitle>
              The building linked to this QR sticker has been removed. If you think this is a mistake, please contact the building manager.
            </Subtitle>
          </>
        )}

        {!loading && !error && info && !info.removed && (
          <>
            <HeroIcon><DomainOutlinedIcon style={{ fontSize: 32 }} /></HeroIcon>
            <Title>Request access to this building</Title>

            <InfoBlock>
              <InfoLabel>Building</InfoLabel>
              <InfoValue>{info.buildingName || '—'}</InfoValue>
              {info.buildingAddress && <InfoSub>{info.buildingAddress}</InfoSub>}
            </InfoBlock>

            {submitted && (
              <SuccessState>
                Your request has been sent to the building's {ROLE_LABELS.owner}s. You'll get an email once it's reviewed.
              </SuccessState>
            )}

            {!submitted && !isAuthed && (
              <>
                <Subtitle>
                  Sign in or create a free account so the building's {ROLE_LABELS.owner}s can identify you and give you access.
                </Subtitle>
                <PrimaryButton onClick={() => stashRedirectAndGo(PATHS.signup)}>
                  Create an account
                </PrimaryButton>
                <SecondaryButton onClick={() => stashRedirectAndGo(PATHS.login)}>
                  Sign in
                </SecondaryButton>
              </>
            )}

            {!submitted && isAuthed && (
              <>
                <Subtitle>
                  Tell us your relationship to the building so its {ROLE_LABELS.owner}s can review your request.
                </Subtitle>
                <Field>
                  <FieldLabel>Your relationship to this building</FieldLabel>
                  <SearchableSelect
                    value={relationType}
                    onChange={v => setRelationType(v)}
                    options={RELATION_OPTIONS}
                    ariaLabel='Your relationship to this building'
                  />
                </Field>
                <PrimaryButton onClick={handleSubmitAccess} disabled={submitting}>
                  {submitting ? 'Sending…' : 'Request access'}
                </PrimaryButton>
                <SecondaryButton onClick={() => navigate(PRIVATE_PATHS.bpList)}>
                  Cancel
                </SecondaryButton>
              </>
            )}
          </>
        )}
      </Card>
    </Page>
  )
}

export default BuildingQrLanding
